import { createTheme } from '@mui/material/styles';

// ── Design tokens ────────────────────────────────────────────────────────────
// Inspired by Linear / Stripe / Vercel. One primary accent, neutral grays,
// semantic colors reserved for status only.

const tokens = {
  primary: {
    main:    '#3E8FA3', // Nexuma brand teal
    light:   '#5AACBE',
    dark:    '#256878',
    soft:    '#E6F4F8', // soft teal tint — for hover & active fills
    onSoft:  '#256878', // text on `soft` — 5.60:1, already AA
    contrastText: '#FFFFFF',
  },
  // `dark` is the strong fill tone. `onSoft` is the *text* tone for use on the
  // matching `soft` tint — `dark` is too light there and lands at 3.0–4.4:1,
  // under the 4.5:1 AA floor for the small type our chips and alerts use.
  // Measured on `soft`: success 5.21, warning 4.84, error 5.91, info 5.57.
  success: { main: '#10B981', light: '#34D399', dark: '#059669', soft: '#ECFDF5', onSoft: '#047857' },
  warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706', soft: '#FFFBEB', onSoft: '#B45309' },
  error:   { main: '#EF4444', light: '#F87171', dark: '#DC2626', soft: '#FEF2F2', onSoft: '#B91C1C' },
  info:    { main: '#0EA5E9', light: '#38BDF8', dark: '#0284C7', soft: '#F0F9FF', onSoft: '#0369A1' },

  // Neutral grays (Tailwind-style)
  grey: {
    50:  '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
};

// ── Motion ───────────────────────────────────────────────────────────────────
// One scale for the whole app: `fast` for state flips that must feel immediate
// (hover, tab, chip), `base` for anything that moves or reveals (drawer, modal,
// timeline growth). Both sit in the 150–220ms band. Every consumer must degrade
// to 0.01ms under `prefers-reduced-motion` — see the CssBaseline block below and
// the `reduceMotion` helper exported at the bottom of this file.
const motion = {
  fast: 160,
  base: 200,
  easing: 'cubic-bezier(0.2, 0, 0.2, 1)',
  // Ready-made transition strings so components don't re-derive them.
  hover: 'background-color 160ms cubic-bezier(0.2,0,0.2,1), border-color 160ms cubic-bezier(0.2,0,0.2,1), color 160ms cubic-bezier(0.2,0,0.2,1)',
  surface: 'box-shadow 200ms cubic-bezier(0.2,0,0.2,1), border-color 200ms cubic-bezier(0.2,0,0.2,1), transform 200ms cubic-bezier(0.2,0,0.2,1)',
};

// ── Focus ────────────────────────────────────────────────────────────────────
// A single visible focus treatment reused by every interactive surface. Keyboard
// users must always see where they are; `:focus-visible` keeps it off mouse
// clicks. Never override this with `outline: none` without a replacement.
const focusRing = {
  '&:focus-visible': {
    outline: `2px solid ${tokens.primary.main}`,
    outlineOffset: 2,
  },
};

const theme = createTheme({
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  },
  palette: {
    mode: 'light',
    primary:    tokens.primary,
    secondary:  { main: tokens.grey[700], light: tokens.grey[500], dark: tokens.grey[900], contrastText: '#fff' },
    error:      tokens.error,
    warning:    tokens.warning,
    info:       tokens.info,
    success:    tokens.success,
    background: { default: tokens.grey[50], paper: '#FFFFFF' },
    text:       { primary: tokens.grey[900], secondary: tokens.grey[600], disabled: tokens.grey[500] },
    divider:    tokens.grey[200],
    grey:       tokens.grey,
    // Expose soft tints for reuse via theme.palette.<color>.soft
  },
  shape: { borderRadius: 12 },
  spacing: 8,
  typography: {
    fontFamily: [
      'Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI',
      'Roboto', 'Helvetica Neue', 'sans-serif',
    ].join(','),
    // Page title — 32px / 700
    h1: { fontWeight: 700, fontSize: '2rem',    letterSpacing: '-0.025em', lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: '1.625rem', letterSpacing: '-0.02em',  lineHeight: 1.25 },
    h3: { fontWeight: 700, fontSize: '1.375rem', letterSpacing: '-0.02em',  lineHeight: 1.3 },
    // Section title — 18px / 600
    h4: { fontWeight: 700, fontSize: '2rem',    letterSpacing: '-0.025em', lineHeight: 1.2 },
    h5: { fontWeight: 600, fontSize: '1.125rem', letterSpacing: '-0.015em', lineHeight: 1.35 },
    h6: { fontWeight: 600, fontSize: '1rem',    letterSpacing: '-0.01em',  lineHeight: 1.4 },
    subtitle1: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.5, color: tokens.grey[500] },
    body1:     { fontWeight: 400, fontSize: '0.875rem',  lineHeight: 1.55 },
    body2:     { fontWeight: 400, fontSize: '0.8125rem', lineHeight: 1.55 },
    caption:   { fontWeight: 500, fontSize: '0.75rem',   lineHeight: 1.4, letterSpacing: '0.01em' },
    overline:  { fontWeight: 600, fontSize: '0.6875rem', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.4 },
    button:    { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', letterSpacing: '-0.005em' },
  },
  // Soft, layered shadows — 2025 SaaS style
  shadows: [
    'none',
    '0 1px 2px rgba(17,24,39,0.04)',
    '0 1px 3px rgba(17,24,39,0.06), 0 1px 2px rgba(17,24,39,0.04)',
    '0 2px 6px rgba(17,24,39,0.06), 0 1px 3px rgba(17,24,39,0.04)',
    '0 4px 12px rgba(17,24,39,0.07), 0 2px 4px rgba(17,24,39,0.04)',
    '0 8px 20px rgba(17,24,39,0.08), 0 2px 6px rgba(17,24,39,0.04)',
    ...Array(19).fill('0 16px 32px rgba(17,24,39,0.10), 0 4px 12px rgba(17,24,39,0.06)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: tokens.grey[50] },
        '::-webkit-scrollbar': { width: 6, height: 6 },
        '::-webkit-scrollbar-track': { background: 'transparent' },
        '::-webkit-scrollbar-thumb': { background: tokens.grey[300], borderRadius: 8 },
        '::-webkit-scrollbar-thumb:hover': { background: tokens.grey[400] },
        '*': { scrollBehavior: 'smooth' },
        'body, #root': { overflowX: 'hidden' },
        // Baseline focus ring for anything MUI doesn't style itself (custom
        // Boxes with tabIndex, links, native elements).
        ':focus-visible': {
          outline: `2px solid ${tokens.primary.main}`,
          outlineOffset: 2,
        },
        // Numbers that must line up in columns — clocks, durations, timeline
        // labels, table figures. Applied via `className="tabular"`.
        '.tabular': { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1' },
        // Reduced motion — collapse every transition/animation instead of
        // removing them, so state still changes but nothing moves.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'default', elevation: 0 },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'saturate(180%) blur(12px)',
          WebkitBackdropFilter: 'saturate(180%) blur(12px)',
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '60px !important',
          paddingLeft: '20px !important',
          paddingRight: '20px !important',
        },
      },
    },
    MuiButton: {
      defaultProps: { size: 'medium', disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          padding: '7px 14px',
          boxShadow: 'none',
          transition: motion.hover + ', box-shadow 160ms cubic-bezier(0.2,0,0.2,1)',
          // Touch targets: 44×44 minimum on coarse pointers (WCAG 2.5.5).
          '@media (pointer: coarse)': { minHeight: 44 },
          '@media (max-width: 600px)': { minHeight: 44, padding: '10px 14px' },
          '&.Mui-disabled': { opacity: 0.5 },
          ...focusRing,
        },
        containedPrimary: {
          boxShadow: '0 1px 2px rgba(62,143,163,0.20)',
          '&:hover': {
            boxShadow: '0 4px 10px rgba(62,143,163,0.28)',
            backgroundColor: tokens.primary.dark,
          },
        },
        outlined: ({ theme }) => ({
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.grey[300],
            backgroundColor: theme.palette.grey[50],
          },
        }),
        outlinedPrimary: {
          borderColor: tokens.grey[200],
          color: tokens.primary.main,
          '&:hover': { borderColor: tokens.primary.main, backgroundColor: tokens.primary.soft },
        },
        text: { color: tokens.grey[700], '&:hover': { backgroundColor: tokens.grey[100] } },
        sizeSmall: { padding: '5px 12px', fontSize: '0.78rem' },
        sizeLarge: { padding: '9px 18px', fontSize: '0.875rem' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          borderRadius: 8,
          transition: motion.hover,
          '&:hover': {
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.text.primary,
          },
          '@media (pointer: coarse)': { minWidth: 44, minHeight: 44 },
          ...focusRing,
        }),
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 12,
          backgroundImage: 'none',
          boxShadow: '0 1px 2px rgba(17,24,39,0.04)',
          transition: motion.surface,
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: '20px', '&:last-child': { paddingBottom: 20 } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12, backgroundImage: 'none' },
        elevation0: { boxShadow: 'none' },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiInputBase-root': {
            fontSize: '0.875rem',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: theme.palette.background.paper,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.grey[300],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: 1,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${tokens.primary.soft}`,
            },
          },
        }),
      },
    },
    MuiFormControl: { defaultProps: { fullWidth: true, size: 'small' } },
    MuiInputBase: { styleOverrides: { root: { fontSize: '0.875rem' } } },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          margin: '1px 8px',
          paddingTop: 7,
          paddingBottom: 7,
          transition: motion.hover,
          ...focusRing,
          '&:hover': { backgroundColor: theme.palette.grey[100] },
          '&.Mui-selected': {
            backgroundColor: tokens.primary.soft,
            color: tokens.primary.dark,
            '&:hover': { backgroundColor: tokens.primary.soft },
            '& .MuiListItemIcon-root': { color: tokens.primary.main },
            '& .MuiListItemText-primary': { fontWeight: 600 },
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 32, color: tokens.grey[500] } },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: '0.875rem', fontWeight: 500, letterSpacing: '-0.005em' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.grey[50],
          '& .MuiTableCell-root': {
            fontWeight: 600,
            fontSize: '0.6875rem',
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            padding: '10px 16px',
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '0.875rem',
          padding: '12px 16px',
          borderColor: theme.palette.divider,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'background-color 0.1s ease',
          '&:hover': { backgroundColor: theme.palette.grey[50] },
          '&:last-child td': { borderBottom: 0 },
        }),
      },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: '0.6875rem',
          height: 22,
          letterSpacing: '0.01em',
          // Explicit properties, not `all` — `all` animates layout properties
          // and causes jank when a chip's label changes width.
          transition: motion.hover,
        },
        // Buzë e definuar (tint i ngjyrës kryesore) që chip-at të mos duken të zbehtë/pa kontur.
        // Teksti përdor `onSoft`, jo `dark` — chip-at janë 0.6875rem dhe `dark`
        // bie nën 4.5:1 mbi tintin e vet.
        colorSuccess: { backgroundColor: tokens.success.soft, color: tokens.success.onSoft, border: `1px solid ${tokens.success.main}55` },
        colorError:   { backgroundColor: tokens.error.soft,   color: tokens.error.onSoft,   border: `1px solid ${tokens.error.main}55` },
        colorWarning: { backgroundColor: tokens.warning.soft, color: tokens.warning.onSoft, border: `1px solid ${tokens.warning.main}55` },
        colorInfo:    { backgroundColor: tokens.info.soft,    color: tokens.info.onSoft,    border: `1px solid ${tokens.info.main}55` },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: 6,
          borderRadius: 999,
          backgroundColor: theme.palette.grey[100],
        }),
        // The fills are light by design: success measures 2.30:1 against the
        // track and warning 1.95:1, both under the 3:1 non-text floor, and
        // darkening the track makes it worse (the track moves toward the fill).
        // What actually carries the information is the *edge* — where the fill
        // stops. A hard 2px shadow in the palette's `onSoft` tone marks that
        // boundary at 4.56–5.88:1 without repainting the bar or changing the
        // visual language. No layout cost: it is a shadow, not a border.
        bar: ({ ownerState, theme }) => {
          const p = theme.palette[ownerState?.color] || theme.palette.primary;
          const edge = p.onSoft || p.dark;
          return {
            borderRadius: 999,
            boxShadow: `2px 0 0 0 ${edge}`,
          };
        },
      },
    },
    MuiDialog: {
      defaultProps: { fullWidth: true, maxWidth: 'sm' },
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: '0 24px 48px rgba(17,24,39,0.14), 0 8px 16px rgba(17,24,39,0.06)',
          '@media (max-width: 600px)': {
            margin: '8px',
            width: 'calc(100% - 16px)',
            maxWidth: 'calc(100% - 16px)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '1rem',
          fontWeight: 600,
          padding: '18px 24px',
          borderBottom: `1px solid ${theme.palette.divider}`,
          '@media (max-width: 600px)': { padding: '16px 20px' },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          '@media (max-width: 600px)': { padding: '16px 20px' },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '14px 24px',
          gap: 8,
          '@media (max-width: 600px)': { padding: '12px 20px', flexWrap: 'wrap' },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '0.875rem',
          fontWeight: 500,
          minHeight: 42,
          padding: '8px 16px',
          color: theme.palette.text.secondary,
          letterSpacing: '-0.005em',
          transition: motion.hover,
          '&:hover': { color: theme.palette.text.primary },
          '&.Mui-selected': { color: theme.palette.text.primary, fontWeight: 600 },
          '@media (pointer: coarse)': { minHeight: 44 },
          '&:focus-visible': {
            outline: `2px solid ${tokens.primary.main}`,
            outlineOffset: -2,
            borderRadius: 6,
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42 },
        indicator: {
          height: 2,
          borderRadius: '2px 2px 0 0',
          backgroundColor: tokens.primary.main,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { fontSize: '0.875rem', paddingTop: 8, paddingBottom: 8 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '0.875rem',
          minHeight: 38,
          borderRadius: 6,
          margin: '2px 6px',
          padding: '6px 10px',
          transition: motion.hover,
          ...focusRing,
          '@media (pointer: coarse)': { minHeight: 44 },
          '&:hover': { backgroundColor: theme.palette.grey[100] },
          '&.Mui-selected': {
            backgroundColor: tokens.primary.soft,
            color: tokens.primary.dark,
            '&:hover': { backgroundColor: tokens.primary.soft },
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          marginTop: 6,
          border: `1px solid ${tokens.grey[200]}`,
          boxShadow: '0 8px 28px rgba(17,24,39,0.10), 0 2px 6px rgba(17,24,39,0.04)',
        },
        list: { padding: '6px 0' },
      },
    },
    MuiAutocomplete: { defaultProps: { size: 'small' } },
    MuiTooltip: {
      defaultProps: { arrow: true, placement: 'top' },
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 6,
          padding: '6px 10px',
          backgroundColor: tokens.grey[900],
        },
        arrow: { color: tokens.grey[900] },
      },
    },
    // Një vend i vetëm për çdo njoftim: poshtë, në qendër. E paracaktuara e
    // MUI-t, poshtë-majtas, bie mbi menynë anësore dhe mbulon zërat e fundit.
    // Kush e cakton vetë, mbetet si është — `defaultProps` nuk mbizotëron.
    MuiSnackbar: {
      defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'center' } },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '12px 16px',
          borderRadius: 10,
          fontWeight: 500,
          border: '1px solid',
          // `border: '1px solid'` pa ngjyrë bie te `currentColor`. Te varianti
          // `standard` kjo nuk vihej re, sepse çdo variant e cakton vetë
          // `borderColor`. Te `filled` s'e caktonte askush: kufiri merrte
          // ngjyrën e TEKSTIT — dhe meqë sfondet tona semantike janë të ndritura,
          // MUI-ja e llogarit tekstin të errët. Rezultati ishte një kuti e gjelbër
          // me kontur thuajse të zi, si ngjitëse. Kjo rreshtë e mbyll atë rrugë;
          // variantet e mëposhtme e caktojnë secili kufirin e vet.
          borderColor: 'transparent',
        },
        // Kufiri merr tonin `main` në vend të `soft` — një alert pa kontur
        // dukej si sfond i thjeshtë dhe humbte si njoftim.
        standardSuccess: { backgroundColor: tokens.success.soft, borderColor: `${tokens.success.main}55`, color: tokens.success.onSoft },
        standardError:   { backgroundColor: tokens.error.soft,   borderColor: `${tokens.error.main}55`,   color: tokens.error.onSoft },
        standardWarning: { backgroundColor: tokens.warning.soft, borderColor: `${tokens.warning.main}55`, color: tokens.warning.onSoft },
        standardInfo:    { backgroundColor: tokens.info.soft,    borderColor: `${tokens.info.main}55`,    color: tokens.info.onSoft },

        // Sfondi i plotë merr tonin `onSoft`, jo `main`. `main` është shumë i
        // ndritur për tekst të bardhë — success bie në 2.06:1, nën çdo prag. Me
        // `onSoft` masen 5.48 (success), 5.94 (error), 4.83 (warning), 5.57
        // (info): AA për tekst normal, dhe e njëjta familje ngjyrash.
        filledSuccess: { backgroundColor: tokens.success.onSoft, color: '#FFFFFF' },
        filledError:   { backgroundColor: tokens.error.onSoft,   color: '#FFFFFF' },
        filledWarning: { backgroundColor: tokens.warning.onSoft, color: '#FFFFFF' },
        filledInfo:    { backgroundColor: tokens.info.onSoft,    color: '#FFFFFF' },

        // Mbushja e brendshme e MUI-t (7–8px lart e poshtë) mblidhej mbi 12px të
        // rrënjës dhe i bënte alertet e larta pa nevojë. Rreshtimi vertikal NUK
        // caktohet këtu: pesë ekrane përdorin `AlertTitle`, dhe një `display:flex`
        // mbi `message` do t'ia vinte titullin e tekstin njërin pranë tjetrit në
        // vend që t'i vendoste njërin mbi tjetrin.
        icon:    { padding: 0, marginRight: 12, opacity: 1 },
        message: { padding: 0, minWidth: 0 },
        action:  { padding: '0 0 0 12px', marginRight: -4, alignItems: 'center' },
      },
    },
    MuiBreadcrumbs: { styleOverrides: { root: { fontSize: '0.8125rem' } } },
    MuiDivider: { styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.divider }) } },
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 600, fontSize: '0.625rem', height: 18, minWidth: 18, padding: '0 5px' },
        dot:   { width: 8, height: 8, borderRadius: '50%' },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: 600,
          fontSize: '0.8125rem',
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.text.secondary,
        }),
      },
    },
  },
});

// Expose soft tints for reuse (theme.palette.primary.soft etc.)
theme.palette.primary.soft = tokens.primary.soft;
theme.palette.success.soft = tokens.success.soft;
theme.palette.warning.soft = tokens.warning.soft;
theme.palette.error.soft   = tokens.error.soft;
theme.palette.info.soft    = tokens.info.soft;

// AA-safe text tone for use on the matching `soft` tint (see the token block).
theme.palette.primary.onSoft = tokens.primary.onSoft;
theme.palette.success.onSoft = tokens.success.onSoft;
theme.palette.warning.onSoft = tokens.warning.onSoft;
theme.palette.error.onSoft   = tokens.error.onSoft;
theme.palette.info.onSoft    = tokens.info.onSoft;

// Motion + focus tokens on the theme, so components read them instead of
// hardcoding their own durations: `theme.motion.base`, `theme.focusRing`.
theme.motion = motion;
theme.focusRing = focusRing;

/**
 * Typographic "time voice" — the one deliberate type treatment that runs across
 * every clock, duration, timesheet figure and timeline label in Nexuma. Numbers
 * that describe time must line up in columns and never reflow as they tick, so
 * they get tabular figures and tighter tracking than surrounding prose.
 *
 * Usage: `sx={{ ...timeType(theme), fontSize: '1.5rem' }}`
 */
export const timeType = () => ({
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum" 1',
  letterSpacing: '-0.015em',
  fontWeight: 700,
});

/**
 * True when the user asked the OS to reduce motion. Components use it to skip
 * JS-driven animation entirely; pure CSS transitions are already neutralised by
 * the CssBaseline media query above.
 */
export const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default theme;
